import rolesConstants, { RolesTypes } from '@utils/rolesConstants';
import {
  ApplicationsConstants,
  motProducts,
} from '@utils/applicationsConstants';

export type CustomerType = 'ROOT' | 'SUPERBROKER' | 'BROKER' | 'CUSTOMER';

export type MenuItemsProps = {
  role: RolesTypes;
  products: ApplicationsConstants[] | undefined;
  whoCantSee?: CustomerType[];
  isLoraWan?: boolean;
  includeInNewLoraWan?: boolean;
  onlyNewLora?: boolean;
  alternativeTitle?: string;
  whoCanSeeAlternativeTitle?: CustomerType[];
  isOnlyUpload?: boolean;
  isOnlyAlgarCustomer?: boolean;
  productThatCustomerCannotSee?: ApplicationsConstants[];
};

interface ActionItems {
  showContracted?: boolean;
  isStock?: boolean;
  customerType?: CustomerType;
  rolesToAccess?: RolesTypes[];
  selectedApp?: ApplicationsConstants;
  isLoraWan?: boolean;
  hasNewLora?: boolean;
}

export const actionsItems = ({
  showContracted,
  isStock,
  customerType,
  rolesToAccess,
  selectedApp,
  isLoraWan,
  hasNewLora,
}: ActionItems): MenuItemsProps[] => {
  const multiConnect = ApplicationsConstants.MULTI_CONNECT;
  const connectOne = ApplicationsConstants.CONNECT_ONE;
  const motEnterprise = ApplicationsConstants.MOT_ENTERPRISE;

  const commonActions: MenuItemsProps[] = [
    {
      role: rolesConstants.NETWORK_RESET,
      products: [...motProducts, multiConnect, connectOne],
    },
    {
      role: rolesConstants.NICKNAME_CHANGE,
      products: [...motProducts, multiConnect, connectOne],
    },
    {
      role: rolesConstants.SIMCARD_RESET,
      products: [...motProducts, multiConnect, connectOne],
    },
    {
      role: rolesConstants.SMS_BLOCK,
      products: [...motProducts, multiConnect, connectOne],
    },
    {
      role: rolesConstants.NETWORK_BLOCK_UNLOCK,
      products: [...motProducts, multiConnect, connectOne],
    },
    {
      role: rolesConstants.CHANGE_APN,
      products: [...motProducts, multiConnect, connectOne],
    },
    {
      role: rolesConstants.SYNC_APN,
      products: [...motProducts, multiConnect, connectOne],
      whoCantSee: ['SUPERBROKER', 'BROKER', 'CUSTOMER'] as CustomerType[],
      isOnlyUpload: true,
    },
    {
      role: rolesConstants.HLR_REPROG,
      products: [...motProducts, multiConnect, connectOne],
      productThatCustomerCannotSee: [motEnterprise],
    },
    {
      role: rolesConstants.CONTRACT_SUSPEND,
      products: [...motProducts, multiConnect, connectOne],
      isOnlyAlgarCustomer: true,
    },
    {
      role: rolesConstants.CANCELLATION,
      products: [...motProducts, multiConnect, connectOne],
      isOnlyAlgarCustomer: true,
    },
    {
      role: rolesConstants.OPERATOR_RESTRICTION,
      products: [...motProducts, multiConnect, connectOne],
    },
    {
      role: rolesConstants.CHANGE_SIMCARD,
      products: [...motProducts, multiConnect, connectOne],
      isOnlyAlgarCustomer: true,
    },
  ];

  const motActions: MenuItemsProps[] = [
    {
      role: rolesConstants.TRANSFER,
      products: motProducts,
      whoCantSee: ['ROOT'] as CustomerType[],
    },
    {
      role: rolesConstants.MSISDN_EXCHANGE,
      products: motProducts,
      whoCantSee: ['ROOT'] as CustomerType[],
    },
    {
      role: rolesConstants.PLASTIC_EXCHANGE,
      products: motProducts,
      whoCantSee: ['ROOT'] as CustomerType[],
    },
    {
      role: rolesConstants.SUSPENSION,
      products: motProducts,
      whoCantSee: ['SUPERBROKER'] as CustomerType[],
    },
    {
      role: rolesConstants.IMEI_LOCK_UNLOCK,
      products: motProducts,
    },
    {
      role: rolesConstants.BROKER_CANCELLATION,
      products: motProducts,
      whoCantSee: ['ROOT', 'SUPERBROKER', 'BROKER'] as CustomerType[],
    },
    {
      role: rolesConstants.SUSPENSION_WITHDRAWAL,
      products: motProducts,
      whoCantSee: ['ROOT', 'SUPERBROKER', 'BROKER'] as CustomerType[],
    },
    {
      role: rolesConstants.MOT_OPERATOR_CHANGE,
      products: motProducts,
    },
    {
      role: rolesConstants.SEND_SMS,
      products: motProducts,
      whoCantSee: ['ROOT'] as CustomerType[],
    },
    {
      role: rolesConstants.BAIT_RENEWAL,
      products: [ApplicationsConstants.MOT_ENTERPRISE],
      whoCantSee: ['ROOT'] as CustomerType[],
      alternativeTitle: `${rolesConstants.BAIT_RENEWAL}-broker`,
      whoCanSeeAlternativeTitle: ['ROOT', 'BROKER'] as CustomerType[],
    },
  ];

  if ((showContracted && customerType !== 'BROKER') || !showContracted) {
    motActions.push({
      role: rolesConstants.ADD_CREDIT_TO_SIMCARD,
      products: motProducts,
      whoCantSee: ['ROOT'] as CustomerType[],
      alternativeTitle: 'credit-purchase',
      whoCanSeeAlternativeTitle: ['CUSTOMER'] as CustomerType[],
    });
  }

  if (isStock) {
    motActions.push({
      role: rolesConstants.MOT_DEACTIVATION,
      products: motProducts,
      whoCantSee: ['ROOT', 'CUSTOMER'] as CustomerType[],
    });
  }

  if (!showContracted) {
    motActions.push(
      {
        role: rolesConstants.FORCE_REACTIVATION,
        products: motProducts,
        whoCantSee: ['ROOT'] as CustomerType[],
      },
      {
        role: rolesConstants.MANUAL_ACTIVATION,
        products: motProducts,
        whoCantSee: ['ROOT'] as CustomerType[],
      },
    );
  }

  const loraWanActions: MenuItemsProps[] = [
    {
      role: rolesConstants.LORAWAN_TRANSFERS,
      products: [
        ApplicationsConstants.MOT_ENTERPRISE,
        ApplicationsConstants.MOT_BUSINESS,
      ],
      whoCantSee: ['ROOT'] as CustomerType[],
      isLoraWan: true,
      includeInNewLoraWan: true,
    },
    {
      role: rolesConstants.LORAWAN_DEACTIVATION,
      products: [
        ApplicationsConstants.LORAWAN,
        ApplicationsConstants.MOT_ENTERPRISE,
        ApplicationsConstants.MOT_BUSINESS,
      ],
      whoCantSee: ['ROOT'] as CustomerType[],
      isLoraWan: true,
      includeInNewLoraWan: true,
    },
    {
      role: rolesConstants.LORAWAN_UPLINK_BLOCK_UNLOCK,
      products: [
        ApplicationsConstants.LORAWAN,
        ApplicationsConstants.MOT_ENTERPRISE,
        ApplicationsConstants.MOT_BUSINESS,
      ],
      isLoraWan: true,
    },
    {
      role: rolesConstants.LORAWAN_DOWNLINK_BLOCK_UNLOCK,
      products: [
        ApplicationsConstants.LORAWAN,
        ApplicationsConstants.MOT_ENTERPRISE,
        ApplicationsConstants.MOT_BUSINESS,
      ],
      isLoraWan: true,
    },
    {
      role: rolesConstants.LORAWAN_CHANGE_DESCRIPTION,
      products: [
        ApplicationsConstants.LORAWAN,
        ApplicationsConstants.MOT_ENTERPRISE,
        ApplicationsConstants.MOT_BUSINESS,
      ],
      isLoraWan: true,
      includeInNewLoraWan: true,
    },
    {
      role: rolesConstants.LORAWAN_CHANGE_TAGS,
      products: [
        ApplicationsConstants.LORAWAN,
        ApplicationsConstants.MOT_ENTERPRISE,
        ApplicationsConstants.MOT_BUSINESS,
      ],
      isLoraWan: true,
      includeInNewLoraWan: true,
    },
    {
      role: rolesConstants.LORAWAN_DOWNLINK_SCHEDULE,
      products: [
        ApplicationsConstants.LORAWAN,
        ApplicationsConstants.MOT_ENTERPRISE,
        ApplicationsConstants.MOT_BUSINESS,
      ],
      isLoraWan: true,
      includeInNewLoraWan: true,
    },
    {
      role: rolesConstants.LORAWAN_SUSPENSION_REACTIVATION,
      products: [
        ApplicationsConstants.LORAWAN,
        ApplicationsConstants.MOT_ENTERPRISE,
        ApplicationsConstants.MOT_BUSINESS,
      ],
      isLoraWan: true,
      includeInNewLoraWan: true,
      onlyNewLora: true,
    },
    {
      role: rolesConstants.LORAWAN_CONNECTING_LINK,
      products: [
        ApplicationsConstants.LORAWAN,
        ApplicationsConstants.MOT_ENTERPRISE,
        ApplicationsConstants.MOT_BUSINESS,
      ],
      isLoraWan: true,
      includeInNewLoraWan: true,
      onlyNewLora: true,
    },
  ].filter(item => {
    if (item.onlyNewLora && !hasNewLora) {
      return false;
    }
    return !hasNewLora || item.includeInNewLoraWan;
  });

  return [...commonActions, ...motActions, ...loraWanActions]?.filter(item => {
    const havePermission = rolesToAccess?.includes(item.role);

    const isOnRightProduct =
      !!selectedApp && item.products?.includes(selectedApp);

    const customerCanSee = !item.whoCantSee?.includes(
      customerType as CustomerType,
    );

    const isCorrectNetwork = isLoraWan ? item?.isLoraWan : !item?.isLoraWan;

    return (
      isOnRightProduct && customerCanSee && havePermission && isCorrectNetwork
    );
  });
};
